import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, Mail, Shield, User as UserIcon, CheckCircle, XCircle, Plus } from 'lucide-react';
import { usersApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  lastLoginAt: string;
  createdAt: string;
}

export default function UserList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'user',
  });
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'user',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users', search],
    queryFn: () => usersApi.getAll({ search }),
  });

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('ユーザーを登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('ユーザーを更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('ユーザーを無効化しました');
    },
    onError: () => toast.error('無効化に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => usersApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('選択したユーザーを無効化しました');
    },
    onError: () => toast.error('一括無効化に失敗しました'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      await Promise.all(ids.map(id => usersApi.update(id, { status })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('ステータスを更新しました');
    },
    onError: () => toast.error('ステータス更新に失敗しました'),
  });

  const inviteMutation = useMutation({
    mutationFn: usersApi.invite,
    onSuccess: (response) => {
      toast.success('招待メールを送信しました');
      setIsInviteModalOpen(false);
      setInviteData({ email: '', role: 'user' });
      // 招待URLをコピー
      navigator.clipboard.writeText(response.data.inviteUrl);
      toast.success('招待URLをクリップボードにコピーしました');
    },
    onError: () => toast.error('招待に失敗しました'),
  });

  const resetForm = () => {
    setFormData({ email: '', name: '', role: 'user' });
    setEditingUser(null);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate(inviteData);
  };

  const handleDelete = (id: string) => {
    if (confirm('このユーザーを無効化しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件のユーザーを無効化しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleBulkActivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, status: 'active' });
  };

  const handleBulkDeactivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, status: 'disabled' });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield size={16} style={{ color: 'var(--error-color)' }} />;
      case 'user':
        return <UserIcon size={16} style={{ color: 'var(--accent-color)' }} />;
      default:
        return <UserIcon size={16} style={{ color: 'var(--text-secondary)' }} />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'user': return 'ユーザー';
      case 'viewer': return '閲覧者';
      default: return role;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">有効</span>;
      case 'pending':
        return <span className="badge badge-warning">保留</span>;
      case 'disabled':
        return <span className="badge badge-error">無効</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const columns = [
    { 
      key: 'name', 
      label: 'ユーザー名', 
      sortable: true,
      render: (row: User) => (
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            <span className="text-white text-sm font-medium">
              {row.name.charAt(0)}
            </span>
          </div>
          <span>{row.name}</span>
        </div>
      ),
    },
    { key: 'email', label: 'メールアドレス' },
    {
      key: 'role',
      label: 'ロール',
      width: '100px',
      render: (row: User) => (
        <div className="flex items-center gap-2">
          {getRoleIcon(row.role)}
          <span>{getRoleLabel(row.role)}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: '状態',
      width: '80px',
      render: (row: User) => getStatusBadge(row.status),
    },
    {
      key: 'lastLoginAt',
      label: '最終ログイン',
      width: '140px',
      render: (row: User) => row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleDateString('ja-JP') : '-',
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: User) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="ユーザー管理"
        breadcrumbs={[{ label: 'システム管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="名前・メールで検索..."
        onApply={() => refetch()}
        actions={
          <div className="flex items-center gap-1.5">
            <button className="btn btn-outline btn-sm" onClick={() => setIsInviteModalOpen(true)}>
              <Mail size={14} />
              招待
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <Plus size={14} />
              ユーザー登録
            </button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={[
          {
            label: '有効化',
            icon: <CheckCircle size={14} />,
            onClick: handleBulkActivate,
            variant: 'primary' as const,
          },
          {
            label: '無効化',
            icon: <XCircle size={14} />,
            onClick: handleBulkDeactivate,
            variant: 'outline' as const,
          },
          {
            label: '一括削除',
            icon: <Trash2 size={14} />,
            onClick: handleBulkDelete,
            variant: 'danger' as const,
          },
        ]}
        getRowId={(row: User) => row.id}
        defaultSortKey="name"
        defaultSortOrder="asc"
        emptyMessage="ユーザーが登録されていません"
      />

      {/* User Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingUser ? 'ユーザー編集' : 'ユーザー登録'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">メールアドレス <span className="text-red-500">*</span></label>
            <input
              type="email"
              className="input-field"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={!!editingUser}
            />
          </div>

          <div>
            <label className="label">ユーザー名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">ロール <span className="text-red-500">*</span></label>
            <select
              className="input-field"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              <option value="admin">管理者</option>
              <option value="user">ユーザー</option>
              <option value="viewer">閲覧者</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingUser ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Invite Modal */}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => { setIsInviteModalOpen(false); setInviteData({ email: '', role: 'user' }); }}
        title="ユーザー招待"
      >
        <form onSubmit={handleInvite} className="space-y-4">
          <div>
            <label className="label">メールアドレス <span className="text-red-500">*</span></label>
            <input
              type="email"
              className="input-field"
              placeholder="user@example.com"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">ロール <span className="text-red-500">*</span></label>
            <select
              className="input-field"
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              required
            >
              <option value="admin">管理者</option>
              <option value="user">ユーザー</option>
              <option value="viewer">閲覧者</option>
            </select>
          </div>

          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              招待メールが送信され、ユーザーは7日以内にアカウントを有効化できます。
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsInviteModalOpen(false); setInviteData({ email: '', role: 'user' }); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviteMutation.isPending}
            >
              <Mail size={16} />
              招待を送信
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}


